import {
	Button,
	Grid,
	IconButton,
	InputAdornment,
	TextField,
	Typography,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import {
	GetContextMenuItemsParams,
	GridApi,
	MenuItemDef,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import React, { useEffect, useState } from "react";
import { useContextMenuMESF } from "../../../../controls";
import { LazyLoading } from "../../../../controls/LazyLoading/LazyLoading";
import { RecipientTypeLegend } from "../../emailTopics/components/RecipientTypeChips";
import { EmailAddress } from "../../emailTopics/models/EmailAddress";
import { useEmailAddressesContext } from "../context/EmailAddressesContext";
import { useStyles } from "../styles/useStyles";
import { CreateEmailAddress } from "./upsertDelete/CreateEmailAddress";
import { DeleteEmailAddress } from "./upsertDelete/DeleteEmailAddress";
import { EditEmailAddress } from "./upsertDelete/EditEmailAddress";
import { useEmailAddressesOptionsFunctions } from "./customHooks/useEmailAddressesOptionsFunctions";
import { useTableData } from "./customHooks/useTableEmailAddresses";

interface Props {
	isLoading: boolean;
	getEmailAddressesFromAPI: () => void;
}

export const TableEmailAddresses = ({
	isLoading,
	getEmailAddressesFromAPI,
}: Props) => {
	const { showContextMenu, registerConfig } = useContextMenuMESF();
	const {
		state: { addresses },
		actions: { setAddressSelected },
	} = useEmailAddressesContext();
	const classes = useStyles();

	const [gridApi, setGridApi] = useState<GridApi | null>(null);
	const [filterValue, setFilterValue] = useState("");
	const [openModalNew, setOpenModalNew] = useState(false);
	const [openModalEditAddress, setOpenModalEditAddress] = useState(false);
	const [openModalDeleteAddress, setOpenModalDeleteAddress] = useState(false);

	const { getMenuOptions } = useEmailAddressesOptionsFunctions({
		setAddressSelected,
		setShowDeleteModal: setOpenModalDeleteAddress,
		setShowCreateModal: setOpenModalNew,
		setShowEditModal: setOpenModalEditAddress,
	});

	const { columnDefs } = useTableData({
		setOpenModalEditAddress,
		setOpenModalDeleteAddress,
		showContextMenu,
	});

	const loadingOverlayComponent = () => (
		<div
			style={{
				width: "100%",
				height: "100%",
				display: "flex",
				justifyContent: "center",
				alignItems: "center",
			}}
		>
			<LazyLoading />
		</div>
	);

	const getContextMenuItems = (
		params: GetContextMenuItemsParams,
	): (string | MenuItemDef)[] => {
		const data = params.node?.data;
		params.api.deselectAll();
		params.node?.setSelected(true);
		showContextMenu(event as any, data, "TableEmailAddresses");
		return [];
	};

	useEffect(() => {
		registerConfig({
			id: "TableEmailAddresses",
			getOptions: getMenuOptions,
		});
	}, []);

	// hideOverlay() also clears the "no rows" overlay, and this table only mounts once loading is
	// done, so calling it unconditionally hid the empty-state message for good.
	useEffect(() => {
		if (!gridApi) return;
		if (isLoading) {
			gridApi.showLoadingOverlay();
		} else if (addresses.length === 0) {
			gridApi.showNoRowsOverlay();
		} else {
			gridApi.hideOverlay();
		}
	}, [isLoading, gridApi, addresses.length]);

	return (
		<Grid
			container
			justifyContent="center"
			alignItems="center"
			className={classes.root}
		>
			<Grid item md={12} xs={12} style={{ padding: "0 15px" }}>
				<Typography
					variant="h5"
					style={{ margin: "10px 0 6px", fontWeight: 600 }}
				>
					Email Addresses
				</Typography>
			</Grid>
			<Grid item md={12} xs={12} style={{ padding: "0 15px" }}>
				<TextField
					fullWidth
					label="Search"
					variant="outlined"
					size="small"
					margin="dense"
					value={filterValue}
					onChange={(event) => {
						setFilterValue(event.target.value);
						gridApi?.setQuickFilter(event.target.value);
					}}
					InputProps={{
						startAdornment: (
							<InputAdornment position="start">
								<IconButton edge="start" type="submit">
									<SearchIcon />
								</IconButton>
							</InputAdornment>
						),
					}}
				/>
			</Grid>

			<Grid
				item
				md={12}
				xs={12}
				style={{ padding: "0 15px" }}
				className={classes.relative}
			>
				<CreateEmailAddress
					show={openModalNew}
					onHide={(shouldUpdate: boolean) => {
						setOpenModalNew(false);
						if (shouldUpdate) getEmailAddressesFromAPI();
					}}
				/>
				<EditEmailAddress
					show={openModalEditAddress}
					onHide={(shouldUpdate: boolean) => {
						setOpenModalEditAddress(false);
						if (shouldUpdate) getEmailAddressesFromAPI();
					}}
				/>
				<DeleteEmailAddress
					show={openModalDeleteAddress}
					onHide={(shouldUpdate: boolean) => {
						setOpenModalDeleteAddress(false);
						if (shouldUpdate) getEmailAddressesFromAPI();
					}}
				/>
				<Grid
					item
					md={12}
					xs={12}
					style={{
						height: "calc(100vh - 360px)",
						minHeight: 240,
						margin: "6px 0 10px",
					}}
				>
					<div
						style={{ height: "100%", width: "100%" }}
						className="ag-theme-balham"
					>
						<AgGridReact
							rowData={addresses}
							columnDefs={columnDefs}
							defaultColDef={{
								sortable: true,
								filter: true,
								resizable: true,
								suppressMenu: true,
							}}
							rowHeight={40}
							headerHeight={34}
							animateRows={true}
							loadingOverlayComponent={loadingOverlayComponent}
							overlayNoRowsTemplate={
								'<span class="ag-overlay-no-rows-center">No email addresses yet</span>'
							}
							getContextMenuItems={(e: any) => getContextMenuItems(e)}
							statusBar={{
								statusPanels: [
									{ statusPanel: "agTotalRowCountComponent", align: "left" },
									{ statusPanel: "agFilteredRowCountComponent", align: "left" },
									{ statusPanel: RecipientTypeLegend, align: "right" },
								],
							}}
							rowSelection="single"
							onRowDoubleClicked={(event) => {
								setAddressSelected(event.data as EmailAddress);
								setOpenModalEditAddress(true);
							}}
							onGridReady={(params) => setGridApi(params.api)}
						/>
					</div>
				</Grid>
				<Grid container justifyContent="flex-end">
					<Grid item md={2} xs={12} className={classes.btnModal}>
						<Button
							variant="contained"
							color="primary"
							onClick={() => setOpenModalNew(!openModalNew)}
							fullWidth
							style={{ marginTop: 10 }}
						>
							NEW ADDRESS
						</Button>
					</Grid>
				</Grid>
			</Grid>
		</Grid>
	);
};
